import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-xl font-bold text-gray-900 mb-3 font-heading">{title}</h2>
    <div className="text-gray-600 text-sm leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function TermsPage() {
  return (
    <>
      <SEO
        title="Terms of Service"
        description="Terms of Service for MDS Transportation LLC — Non-Emergency Medical Transportation in Indianapolis and Fort Wayne."
        path="/terms"
      />

      {/* Page hero — sits directly under the shared navbar */}
      <div className="bg-purple-700 py-14 px-4">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h1 className="text-4xl font-bold font-heading mb-2">Terms of Service</h1>
          <p className="text-purple-200 text-sm">Last updated: January 1, 2024</p>
        </div>
      </div>

      {/* Content */}
      <div className="bg-gray-50 py-14 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="card shadow-sm border border-gray-100">
            <Section title="1. Acceptance of Terms">
              <p>By accessing or using any services provided by <strong>MDS Transportation LLC</strong> ("Company," "we," "us," or "our"), including our website at mdstransportation.com and our transportation services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.</p>
            </Section>

            <Section title="2. Services Provided">
              <p>MDS Transportation LLC provides non-emergency medical transportation (NEMT) services including but not limited to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Transportation to and from medical appointments</li>
                <li>Wheelchair-accessible transportation</li>
                <li>Senior and assisted transportation</li>
                <li>Hospital discharge transportation</li>
                <li>Recurring transportation for ongoing treatments</li>
              </ul>
              <p>We do not provide emergency medical services. In the event of a medical emergency, please call 911 immediately.</p>
            </Section>

            <Section title="3. Booking & Cancellations">
              <p>All bookings must be made in advance through our website or by calling +1 (317) 982-1959. We reserve the right to decline any booking at our discretion.</p>
              <p>Cancellations must be made at least <strong>2 hours before</strong> the scheduled pickup time. Late cancellations or no-shows may be subject to a cancellation fee. Recurring bookings may be cancelled with 24 hours' notice.</p>
            </Section>

            <Section title="4. Passenger Responsibilities">
              <p>Passengers agree to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Be ready at the pickup location at the agreed time</li>
                <li>Provide accurate pickup and destination information</li>
                <li>Disclose any mobility needs or special requirements at time of booking</li>
                <li>Treat drivers and other passengers with respect</li>
                <li>Refrain from smoking, alcohol consumption, or abusive behavior in our vehicles</li>
              </ul>
            </Section>

            <Section title="5. Liability Limitation">
              <p>MDS Transportation LLC carries full commercial vehicle insurance. However, our liability for any claim arising from transportation services shall be limited to the cost of the ride in question. We are not responsible for delays caused by traffic, weather, or circumstances beyond our control.</p>
              <p>We are not liable for any personal belongings left in our vehicles. Passengers are responsible for their own medical devices, medications, and personal items.</p>
            </Section>

            <Section title="6. Privacy">
              <p>Your privacy is important to us. Please review our <Link to="/privacy" className="text-purple-600 hover:underline">Privacy Policy</Link> to understand how we collect, use, and protect your personal information.</p>
            </Section>

            <Section title="7. Payment">
              <p>Payment is due at the time of service unless prior billing arrangements have been made. We accept cash, credit/debit cards, and approved insurance. Any disputed charges must be reported within 7 days of the service date.</p>
            </Section>

            <Section title="8. Modifications">
              <p>We reserve the right to modify these Terms of Service at any time. Changes will be posted on our website with an updated revision date. Continued use of our services following any changes constitutes acceptance of the new terms.</p>
            </Section>

            <Section title="9. Contact">
              <p>For questions about these Terms of Service, please contact us:</p>
              <div className="bg-gray-50 rounded-lg p-4 mt-2 text-sm">
                <p><strong>MDS Transportation LLC</strong></p>
                <p>5632 W Washington Street, Indianapolis, IN 46241</p>
                <p>📞 <a href="tel:+13179821959" className="text-purple-600">+1 (317) 982-1959</a></p>
                <p>📧 <a href="mailto:info@mdstransportation.com" className="text-purple-600">info@mdstransportation.com</a></p>
              </div>
            </Section>
          </div>

          <div className="text-center mt-8">
            <Link to="/privacy" className="text-purple-600 hover:underline text-sm">
              View Privacy Policy →
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
